import { Drawer, Box, Typography, IconButton, Divider } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { MdClose } from 'react-icons/md';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import Input from '@/components/Common/Input';
import Select from '@/components/Common/Select';
import Button from '@/components/Common/Button';
import TelInput from '@/components/Common/TelInput';
import { useCreateOrUpdateAgencyOnboardingForm } from '@/apis/agency/businessOnboarding/hooks';
import { onboardingFormSchema, OnboardingFormSchema } from '@/utils/schema/validator/OnboardingFormValidation';
import { useToast } from '@/hooks/toast';
import { useGetCountries, useGetStates } from '@/apis/country/hook';
import { useGetAvailableIntegrations } from '@/apis/agency/integrations/hook';
import { useGetTelephonyProviders } from '@/apis/agency/providers/hook';
import { useGetIndustries, useGetSubIndustries } from '@/apis/industries/hook';

interface GenerateOnboardingFormDrawerProps {
  open: boolean;
  onClose: () => void;
}

export default function GenerateOnboardingFormDrawer({ open, onClose }: GenerateOnboardingFormDrawerProps) {
  const { showToast, ToastComponent } = useToast();

  const { control, handleSubmit, reset, watch } = useForm<OnboardingFormSchema>({
    resolver: zodResolver(onboardingFormSchema),
    mode: 'onBlur',
    defaultValues: {
      onboarding_form: {
        business_legal_name: '',
        website_url: '',
        primary_contact_name: '',
        primary_contact_email: '',
        primary_contact_phone: '',
        ticket_reference: '',
        available_integration_ids: [],
        address: {
          street_address: '',
          city: '',
          state_province_region: '',
          postal_zip_code: '',
          country: '',
          time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone
        },
        current_business_solution: {
          telephony_voip_provider: '',
          other_providers: ''
          // ehr_emr_pms_crm: ''
        },
        faq_question_set: '',
        source: 'organic'
      }
    }
  });

  const countryCode = watch('onboarding_form.address.country');
  const { data: countries, isLoading: isCountriesLoading } = useGetCountries();
  const { data: states, isLoading: isStatesLoading } = useGetStates(countryCode || '');
  const { data: providers, isLoading: isProvidersLoading } = useGetTelephonyProviders();
  const { data: industries, isLoading: loadingIndustries } = useGetIndustries();

  const selectedIndustry = watch('onboarding_form.industry');
  const { data: subIndustries, isLoading: subIndustriesLoading } = useGetSubIndustries(selectedIndustry);

  const { data: availableIntegrations } = useGetAvailableIntegrations({
    per_page: 999,
    filter: { industries_contain: selectedIndustry }
  });
  const ehrSystems = availableIntegrations?.data || [];

  const { mutate: createOnboardingForm, isPending } = useCreateOrUpdateAgencyOnboardingForm(false);

  const onSubmit = (data: OnboardingFormSchema) => {
    const formData = {
      ...data,
      onboarding_form: {
        ...data.onboarding_form
      }
    };

    createOnboardingForm(formData as any, {
      onSuccess: () => {
        showToast('Onboarding form link generated successfully', 'success');
        reset();
        onClose();
      },
      onError: (error: any) => {
        const errorMessage =
          error?.response?.data?.message || error?.message || 'Failed to generate onboarding form link';
        showToast(errorMessage, 'error');
      }
    });
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <>
      <Drawer
        anchor='right'
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 600 },
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <Box px={3} py={2} display='flex' justifyContent='space-between' alignItems='center'>
          <Typography variant='h6'>Generate Onboarding Form Link</Typography>
          <IconButton onClick={handleClose}>
            <MdClose />
          </IconButton>
        </Box>

        <Divider />

        <Box
          component='form'
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            overflow: 'hidden'
          }}
        >
          <Box flex={1} overflow='auto' px={3} py={2}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Input
                  name='onboarding_form.business_legal_name'
                  label='Business Legal Name'
                  control={control}
                  required
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Select
                  name='onboarding_form.industry'
                  label='Industry'
                  control={control}
                  disabled={loadingIndustries}
                  required
                  fullWidth
                  options={industries ?? []}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <Input name='onboarding_form.website_url' label='Website URL' control={control} required fullWidth />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <Input
                  name='onboarding_form.primary_contact_name'
                  label='Primary Contact Person'
                  control={control}
                  required
                  fullWidth
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <Input
                  name='onboarding_form.primary_contact_email'
                  label='Primary Contact Email'
                  control={control}
                  required
                  fullWidth
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TelInput
                  name='onboarding_form.primary_contact_phone'
                  label='Primary Contact Phone'
                  defaultCountry='US'
                  control={control}
                  required
                  fullWidth
                />
              </Grid>

              <Grid size={12}>
                <Typography fontWeight={500}>Billing Address</Typography>
              </Grid>

              <Grid size={12}>
                <Input
                  name='onboarding_form.address.street_address'
                  label='Street Address'
                  control={control}
                  required
                  fullWidth
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <Input name='onboarding_form.address.city' label='City' control={control} required fullWidth />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <Input
                  name='onboarding_form.address.postal_zip_code'
                  label='Postal / Zip Code'
                  control={control}
                  required
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Select
                  name='onboarding_form.address.country'
                  label='Country'
                  control={control}
                  required
                  fullWidth
                  isLoading={isCountriesLoading}
                  options={countries || []}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Select
                  key={countryCode}
                  name='onboarding_form.address.state_province_region'
                  label='State / Province / Region'
                  control={control}
                  required
                  fullWidth
                  isLoading={isStatesLoading}
                  options={states || []}
                />
              </Grid>
              <Grid size={12}>
                <Typography fontWeight={500}>Current Business Solution</Typography>
              </Grid>

              <Grid size={12}>
                <Select
                  name='onboarding_form.current_business_solution.telephony_voip_provider'
                  label='Telephony / VoIP Provider'
                  control={control}
                  required
                  fullWidth
                  disabled={isProvidersLoading}
                  options={providers?.telephony_voip_providers ?? []}
                />
              </Grid>

              {watch('onboarding_form.current_business_solution.telephony_voip_provider') === 'other' && (
                <Grid size={12}>
                  <Input
                    name='onboarding_form.current_business_solution.other_providers'
                    label='Other'
                    control={control}
                    fullWidth
                    placeholder='Enter telephony/voip provider'
                  />
                </Grid>
              )}

              <Grid size={12}>
                <Select
                  name='onboarding_form.available_integration_ids[0]'
                  label='EHR / EMR / PMS / CRM'
                  control={control}
                  required
                  fullWidth
                  options={[
                    { label: 'None', value: ' ' },
                    ...ehrSystems.flatMap((system) =>
                      system.id !== undefined
                        ? [
                            {
                              label: system.name,
                              value: system.id
                            }
                          ]
                        : []
                    )
                  ]}
                />
              </Grid>

              <Grid size={12}>
                <Typography fontWeight={500}>Frequently Asked Questions Set</Typography>
              </Grid>

              <Grid size={12}>
                <Select
                  name='onboarding_form.faq_question_set'
                  label='Category'
                  control={control}
                  disabled={subIndustriesLoading}
                  required
                  fullWidth
                  options={subIndustries ?? []}
                />
                <Typography variant='body2' color='text.secondary'>
                  Select a predefined FAQ question set that will be pre-populated in the form.
                </Typography>
              </Grid>
              <Grid size={12}>
                <Typography fontWeight={500}>Ticket Reference</Typography>
              </Grid>
              <Grid size={12}>
                <Input
                  name='onboarding_form.ticket_reference'
                  label='Link'
                  control={control}
                  fullWidth
                  placeholder='https://github.com/org/repo/issue123'
                />
                <Typography variant='body2' color='text.secondary'>
                  Use this field to connect your internal onboarding ticket URL like Github, Notion, Trello etc.
                </Typography>
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* Footer */}
          <Box px={3} py={2} display='flex' justifyContent='flex-end' gap={2}>
            <Button variant='outlined' onClick={handleClose} type='button'>
              Cancel
            </Button>

            <Button type='submit' variant='contained' disabled={isPending}>
              {isPending ? 'Generating...' : 'Generate Link'}
            </Button>
          </Box>
        </Box>
      </Drawer>
      {ToastComponent}
    </>
  );
}
